/**
 * ==========================================================================
 *  THE THREE BOXES, THE PLATE, AND WHAT COMES BACK UP
 * ==========================================================================
 *  Everything the aamupala minigame puts on the screen, on one 2D canvas over
 *  the frame. It is in `src/ui` with the HUD and the blood, and it is not a
 *  screen in the `refreshScreens()` sense: there is nothing to navigate and
 *  nothing to decide. `Game` hands it the pan's view and how long ago Kimmo ate
 *  a vegetable, and that is the entire interface. A restart is two nulls.
 *
 *  Why a canvas rather than the DOM the rest of `src/ui` is built out of:
 *  because what is on it is *drawn*. Three boxes of food, a plate filling up
 *  and a man being sick are pictures, and the alternative to drawing them is
 *  an asset pipeline the rest of this game does not have either - the textures
 *  are painted onto a canvas at load and the sounds are synthesised, and this
 *  is the same decision one layer up.
 *
 *  Nothing in here can change the run. The boxes are `Aamupala`'s, the plate
 *  is `Aamupala`'s, and the only thing this file decides is where they are on
 *  the screen. `src/render/Oksennus.ts` owns the arithmetic of the mess, the
 *  same way `Blood.ts` owns the blood's; this is the brush.
 * ==========================================================================
 */

import { OKSENNUS } from '../config/gameplay';
import { FOODS, type Food } from '../config/aamupala';
import type { IntoxicationLevel } from '../config/intoxication';
import {
  VOMIT_DURATION,
  goutPose,
  goutSeeds,
  nausea,
  sampleGout,
  type GoutPose,
  type GoutSeed,
} from '../render/Oksennus';
import { mulberry32 } from '../utils/prng';
import type { AamupalaView } from '../world/Aamupala';
import { el, setHidden } from './dom';
import { pinMotion } from './pinMotion';

/** Seeded once, at load. See `goutSeeds` for why it may never be reseeded. */
const VOMIT_SEED = 0x0ce55;

/** How many segments a gout's outline is drawn with. */
const OUTLINE_STEPS = 24;

/** Where the row of boxes sits, in fractions of the frame's height. */
const BOX = {
  /** Side of one box, and the gap between two. */
  size: 0.15,
  gap: 0.035,
  /** How far the bottom of the row is off the bottom of the frame. */
  bottom: 0.1,
  /** How far the boxes jump about while the lasta is going. */
  shake: 0.012,
  /** The countdown bar under the row: how thick, and how far under. */
  barHeight: 0.011,
  barGap: 0.028,
  /**
   * The name strip along the bottom of a box: how tall the letters are and
   * where their feet sit, both as fractions of the box.
   *
   * `nameSize` is a ceiling and not the figure that gets used - see
   * `nameFont`, which shrinks it until the longest of the three words fits
   * inside `nameRoom`. All three boxes then draw at that one size, because
   * three words at three sizes is a row that reads as three different things.
   */
  nameSize: 0.13,
  nameBaseline: 0.945,
  nameRoom: 0.84,
} as const;

/** And the plate, above them. */
const PLATE = {
  /** Radius across, and how squashed - it is a plate seen from in front. */
  radius: 0.095,
  squash: 0.42,
  /** How far the plate's middle is above the top of the boxes. */
  above: 0.085,
  /** And how far above the plate the line telling you what to do sits. */
  labelAbove: 0.055,
} as const;

/**
 * The line over the plate. It names the mistake rather than the goal, because
 * the goal is the one thing the overlay already shows: the plate above the row
 * fills a makkara at a time, so "how many" is drawn rather than written. What
 * is not drawn anywhere is that pressing the wrong box is worse than pressing
 * nothing - `AAMUPALA.vomitDuration` against a lost round - and half a second
 * is not long enough to learn that twice.
 */
const INSTRUCTION = 'ÄLÄ VALITSE VEGEPASKAA';

/**
 * What each box says it has in it.
 *
 * The pictures came first and the words are underneath them rather than
 * instead of them, which is the point: at three quarters of a second a
 * silhouette is what the eye actually uses, and the word is what settles the
 * pairs the silhouettes cannot settle on their own. Three browny lumps and two
 * pink ones are a falafel and a makkara at any size the row can be drawn at,
 * and a heap of shreds is a pulled pork or a härkis depending on how brown it
 * is - `AAMUPALA.pickDuration` is not long enough to lean in and check either
 * of them.
 *
 * `makkara` reads LIHAMAKKARA and not MAKKARA, because MAKKARA next to FALAFEL
 * is two words that both mean "a lump of something" to somebody reading them
 * at speed. The half of the word that is doing the work is the meat. PULLED
 * PORK needs no such help and gets none: the word nobody has to parse is the
 * one with PORK in it.
 */
const NAMES: Record<Food, string> = {
  makkara: 'LIHAMAKKARA',
  pihvi: 'PIHVI',
  'pulled-pork': 'PULLED PORK',
  falafel: 'FALAFEL',
  parsakaali: 'PARSAKAALI',
  tofu: 'TOFU',
  harkis: 'HÄRKIS',
  munakoiso: 'MUNAKOISO',
};

function rgba(hex: number, alpha: number): string {
  return `rgba(${(hex >> 16) & 255}, ${(hex >> 8) & 255}, ${hex & 255}, ${alpha})`;
}

const VOMIT_CORE = rgba(OKSENNUS.core, 1);
const VOMIT_EDGE = rgba(OKSENNUS.edge, 1);

/** A gout's outline, in its own frame: long the way it went, and pointed. */
function outlineRadius(seed: GoutSeed, theta: number): number {
  let wobble = 1;
  for (let lobe = 0; lobe < seed.lobes.length; lobe++) {
    wobble += seed.lobes[lobe].amplitude * Math.sin((lobe + 2) * theta + seed.lobes[lobe].phase);
  }
  const along = Math.cos(theta);
  return wobble * (1 + seed.stretch * along) + seed.tail * Math.pow(Math.max(0, along), 6);
}

export class AamupalaOverlay {
  readonly root = el('canvas', 'aamupala');

  private readonly ctx = this.root.getContext('2d');
  /**
   * The gouts are drawn sharp in here and stamped onto the frame through the
   * blur in one `drawImage`. A canvas `filter` is not one blur, it is a blur
   * per fill - a pass per gout, trail and speck, per frame - and the station
   * has to stay sharp on the same canvas, so the blur cannot live on the
   * element the way the blood's does. One filtered stamp is the whole price.
   */
  private readonly messCanvas = document.createElement('canvas');
  private readonly messCtx = this.messCanvas.getContext('2d');
  private readonly seeds: GoutSeed[] = goutSeeds(mulberry32(VOMIT_SEED));
  /** One pose, reused by every gout on every frame. See `sampleGout`. */
  private readonly pose: GoutPose = goutPose();

  /** CSS pixels, and the backing store's ratio to them. */
  private width = 0;
  private height = 0;
  private ratio = 1;
  /** `OKSENNUS.softness` as a canvas filter, rebuilt when the window resizes. */
  private softness = 'none';
  private showing = false;

  constructor() {
    setHidden(this.root, true);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.root);
  }

  setSize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    // The same cap the renderer and the blood use. Two different ratios in one
    // window is a way of finding out that one of them was wrong on somebody
    // else's display.
    this.ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.root.width = Math.max(1, Math.round(width * this.ratio));
    this.root.height = Math.max(1, Math.round(height * this.ratio));
    this.messCanvas.width = this.root.width;
    this.messCanvas.height = this.root.height;
    this.softness = `blur(${(OKSENNUS.softness * height).toFixed(2)}px)`;
  }

  /**
   * `view` is the pan while there is anything to show and null the rest of the
   * time; `vomitAge` is seconds since the mouthful, or null.
   *
   * The two are separate arguments rather than one, because they end at
   * different moments: the pan is back to mixing while Kimmo is still bringing
   * the last one up, and the mess is what says why nothing is happening.
   */
  render(view: AamupalaView | null, vomitAge: number | null, seconds: number, dose: IntoxicationLevel): void {
    const sick = vomitAge !== null && vomitAge >= 0 && vomitAge < VOMIT_DURATION;
    if (!view && !sick) {
      if (this.showing) {
        this.showing = false;
        setHidden(this.root, true);
      }
      return;
    }
    if (!this.showing) {
      this.showing = true;
      setHidden(this.root, false);
    }
    this.paint(view, sick ? vomitAge : null, pinMotion(seconds, dose));
  }

  private paint(view: AamupalaView | null, vomitAge: number | null, motion: ReturnType<typeof pinMotion>): void {
    const ctx = this.ctx;
    if (!ctx) return;

    ctx.setTransform(this.ratio, 0, 0, this.ratio, 0, 0);
    ctx.clearRect(0, 0, this.width, this.height);

    if (view) this.station(ctx, view, motion);
    if (vomitAge !== null) this.mess(ctx, vomitAge);

    ctx.globalAlpha = 1;
    ctx.filter = 'none';
  }

  /* --------------------------------------------------------- the station -- */

  /** The plate, the row of boxes, and the second that is left to choose in. */
  private station(ctx: CanvasRenderingContext2D, view: AamupalaView, motion: ReturnType<typeof pinMotion>): void {
    const h = this.height;
    const size = BOX.size * h;
    const gap = BOX.gap * h;
    const row = size * 3 + gap * 2;
    const left = (this.width - row) / 2;
    const bottom = h * (1 - BOX.bottom);
    const top = bottom - size;

    const plateY = top - PLATE.above * h - PLATE.radius * h * PLATE.squash;
    this.plate(ctx, this.width / 2, plateY, view);

    // Above the plate rather than under it. Under it is where the boxes are,
    // and a line of text across the one thing with a second on its clock is
    // worse than no line at all.
    ctx.font = `600 ${(0.024 * h).toFixed(0)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = 'rgba(244, 241, 232, 0.8)';
    ctx.fillText(
      INSTRUCTION,
      this.width / 2,
      plateY - PLATE.radius * h * PLATE.squash - PLATE.labelAbove * h,
    );

    // Match the keyboard's percentage translation and rotation around the row's centre.
    ctx.save();
    ctx.translate(this.width / 2 + row * motion.x / 100, top + size / 2 + size * motion.y / 100);
    ctx.rotate(motion.rotation * Math.PI / 180);
    ctx.translate(-this.width / 2, -top - size / 2);

    const mixing = view.phase === 'mixing';
    for (let slot = 0; slot < 3; slot++) {
      // While the lasta is going the boxes jump about and cycle what is in
      // them, which is the whole of what "mixing" means to a pair of eyes: the
      // hand knows there is nothing worth pressing yet.
      const jitter = mixing
        ? Math.sin(view.progress * Math.PI * 14 + slot * 2.1) * BOX.shake * h
        : 0;
      const x = left + slot * (size + gap);
      const shown = mixing ? this.scrambled(view, slot) : view.boxes[slot];
      this.box(ctx, x, top + jitter, size, slot, shown, view.phase);
    }
    ctx.restore();

    // The window, as a bar that empties under the row. It is the only thing on
    // screen that says how much of the second is left, and one second is not
    // long enough to count.
    if (view.phase === 'choosing') {
      const barTop = bottom + BOX.barGap * h;
      const barHeight = BOX.barHeight * h;
      ctx.fillStyle = 'rgba(20, 26, 36, 0.55)';
      ctx.fillRect(left, barTop, row, barHeight);
      ctx.fillStyle = '#e3b23c';
      ctx.fillRect(left, barTop, row * (1 - view.progress), barHeight);
    }
  }

  /**
   * What a box appears to hold mid-shuffle.
   *
   * Off the phase's own progress rather than off a generator, so it is the same
   * animation on every machine playing the same replay - and, more to the
   * point, so that nothing on this canvas is ever a source of randomness. The
   * hand is `Aamupala`'s and only `Aamupala`'s.
   */
  private scrambled(view: AamupalaView, slot: number): Food {
    const tick = Math.floor(view.progress * 11) + slot;
    return view.boxes[tick % view.boxes.length];
  }

  /** One box: the tray, the number on it, and what is in it. */
  private box(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    slot: number,
    food: Food,
    phase: AamupalaView['phase'],
  ): void {
    const live = phase === 'choosing';
    ctx.fillStyle = live ? 'rgba(28, 34, 44, 0.86)' : 'rgba(24, 28, 36, 0.66)';
    ctx.fillRect(x, y, size, size);
    ctx.lineWidth = Math.max(1, size * 0.026);
    ctx.strokeStyle = live ? 'rgba(227, 178, 60, 0.95)' : 'rgba(160, 168, 180, 0.5)';
    ctx.strokeRect(x, y, size, size);

    ctx.save();
    // Up a little from the middle of the tray, because the bottom of it is the
    // name now. The helping is smaller for the same reason, and it can afford
    // to be: what the picture has to do is be one of three shapes.
    ctx.translate(x + size / 2, y + size * 0.48);
    // Dimmed rather than hidden while the pan is still going: the boxes are
    // there, and what is in them is what you cannot read yet.
    ctx.globalAlpha = live ? 1 : 0.45;
    this.food(ctx, food, size * 0.3);
    ctx.restore();

    ctx.globalAlpha = 1;
    ctx.textAlign = 'center';
    ctx.font = `700 ${(size * 0.22).toFixed(0)}px system-ui, sans-serif`;
    ctx.textBaseline = 'top';
    ctx.fillStyle = live ? '#e3b23c' : 'rgba(200, 206, 216, 0.6)';
    ctx.fillText(String(slot + 1), x + size / 2, y + size * 0.045);

    // And the word, along the bottom of the tray. It cycles with the picture
    // while the lasta is going and dims with it, because a name that stood
    // still through the shuffle would be the answer printed under a box that
    // is still pretending not to have one.
    ctx.font = this.nameFont(ctx, size);
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = live ? 'rgba(244, 241, 232, 0.92)' : 'rgba(200, 206, 216, 0.45)';
    ctx.fillText(NAMES[food], x + size / 2, y + size * BOX.nameBaseline);
  }

  /**
   * The font the three names are drawn in: the biggest that fits the longest
   * of them inside `BOX.nameRoom`.
   *
   * Measured off the longest word there *is* rather than off the word this box
   * happens to be showing, so the size is a property of the row and not of the
   * hand - a FALAFEL that grew because it is short would be a box you could
   * read the answer off before you read the word.
   */
  private nameFont(ctx: CanvasRenderingContext2D, size: number): string {
    const face = (px: number): string => `700 ${px.toFixed(2)}px system-ui, sans-serif`;
    const room = size * BOX.nameRoom;
    const px = size * BOX.nameSize;
    ctx.font = face(px);
    let widest = 0;
    for (const food of FOODS) widest = Math.max(widest, ctx.measureText(NAMES[food]).width);
    return face(widest > room ? (px * room) / widest : px);
  }

  /**
   * One helping, drawn round the origin at roughly `r` across.
   *
   * Eight of them now, and what they have to do has not changed: be one shape
   * at a glance. The meats are pink-to-red and solid, the vegetables are green,
   * beige or purple, and the two pairs that fight are on purpose - falafel
   * against makkara, and härkis against pulled pork. Both are settled by the
   * word under the box rather than by the picture, which is what the word is
   * for. See `NAMES`.
   */
  private food(ctx: CanvasRenderingContext2D, food: Food, r: number): void {
    switch (food) {
      case 'makkara':
        // Two of them, side by side and barely off parallel, because one
        // sausage on its own is a finger and two crossed ones are a chevron.
        for (const [angle, offset] of [
          [-0.12, -r * 0.34],
          [0.1, r * 0.34],
        ] as const) {
          ctx.save();
          ctx.translate(0, offset);
          ctx.rotate(angle);
          sausage(ctx, r * 1.7, r * 0.4);
          ctx.restore();
        }
        return;
      case 'pihvi': {
        // A slab: wider than it is tall, seared dark, with the fat along one
        // edge and two bars off the grate. The bars are what make it meat
        // rather than a stone - nothing else in the row has a straight line
        // burnt across it.
        ctx.fillStyle = '#7d3320';
        ellipse(ctx, 0, r * 0.05, r * 0.95, r * 0.68);
        ctx.fill();
        ctx.fillStyle = '#e8d6b4';
        ctx.save();
        ctx.beginPath();
        ellipse(ctx, 0, r * 0.05, r * 0.95, r * 0.68);
        ctx.clip();
        ctx.beginPath();
        ctx.ellipse(r * 0.72, r * 0.4, r * 0.7, r * 0.15, -0.42, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(52, 20, 12, 0.75)';
        ctx.lineWidth = r * 0.13;
        for (const at of [-r * 0.28, r * 0.16]) {
          ctx.beginPath();
          ctx.moveTo(-r * 0.9, at + r * 0.2);
          ctx.lineTo(r * 0.9, at - r * 0.2);
          ctx.stroke();
        }
        ctx.restore();
        return;
      }
      case 'pulled-pork': {
        // A heap of strands, piled rather than laid out: the shape is the
        // heap, and the strands are only what tells it from a lump. Redder
        // than the härkis beside it and twice as long in the strand, which is
        // the whole of what separates the two at speed.
        for (const [x, y, angle, length, shade] of [
          [-r * 0.34, r * 0.42, 0.12, 1.2, '#8f3d1c'],
          [r * 0.36, r * 0.34, -0.22, 1.05, '#a84d24'],
          [-r * 0.06, r * 0.24, 0.3, 1.3, '#7c3317'],
          [-r * 0.36, r * 0.06, -0.18, 1.0, '#a04720'],
          [r * 0.28, r * 0.04, -0.06, 1.15, '#b25a2c'],
          [-r * 0.08, -r * 0.16, 0.38, 1.25, '#8a3a1a'],
          [r * 0.3, -r * 0.34, -0.3, 1.0, '#a84d24'],
          [-r * 0.3, -r * 0.4, -0.44, 0.9, '#93411e'],
          [r * 0.0, -r * 0.52, 0.16, 1.1, '#7c3317'],
        ] as const) {
          ctx.fillStyle = shade;
          ctx.beginPath();
          ctx.ellipse(x, y, r * 0.48 * length, r * 0.105, angle, 0, Math.PI * 2);
          ctx.fill();
        }
        return;
      }
      case 'parsakaali': {
        // A stalk with three heads on it. Drawn as one silhouette rather than
        // as botany: at this size a floret is a circle and the tree is the
        // arrangement of them.
        ctx.fillStyle = '#7a8f4a';
        ctx.fillRect(-r * 0.16, -r * 0.1, r * 0.32, r * 0.95);
        ctx.fillStyle = '#3f6b2f';
        for (const [cx, cy, rr] of [
          [0, -r * 0.55, r * 0.5],
          [-r * 0.55, -r * 0.2, r * 0.42],
          [r * 0.55, -r * 0.22, r * 0.4],
        ] as const) {
          ctx.beginPath();
          ctx.arc(cx, cy, rr, 0, Math.PI * 2);
          ctx.fill();
        }
        return;
      }
      case 'falafel': {
        // Three balls, and the speckle that is the whole of how a falafel is
        // told from a meatball at a glance.
        for (const [cx, cy] of [
          [-r * 0.5, r * 0.28],
          [r * 0.5, r * 0.28],
          [0, -r * 0.42],
        ] as const) {
          ctx.fillStyle = '#8a7a3e';
          ctx.beginPath();
          ctx.arc(cx, cy, r * 0.52, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#5d6b30';
          for (let i = 0; i < 5; i++) {
            const a = (i / 5) * Math.PI * 2 + cx;
            ctx.beginPath();
            ctx.arc(cx + Math.cos(a) * r * 0.24, cy + Math.sin(a) * r * 0.24, r * 0.07, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        return;
      }
      case 'tofu': {
        // Three cubes, and cubes are the point: it is the only thing in the
        // row with a corner on it, and nothing that came off a pig is that
        // square. Each one is a front face with a lid drawn on top of it, so
        // that it reads as a block rather than as a white card.
        for (const [x, y, side] of [
          [-r * 0.42, r * 0.36, r * 0.62],
          [r * 0.36, r * 0.26, r * 0.54],
          [-r * 0.02, -r * 0.34, r * 0.58],
        ] as const) {
          const lid = side * 0.34;
          ctx.fillStyle = '#efe9d5';
          ctx.fillRect(x - side / 2, y - side / 2, side, side);
          ctx.fillStyle = '#fbf7e9';
          ctx.beginPath();
          ctx.moveTo(x - side / 2, y - side / 2);
          ctx.lineTo(x - side / 2 + lid, y - side / 2 - lid);
          ctx.lineTo(x + side / 2 + lid, y - side / 2 - lid);
          ctx.lineTo(x + side / 2, y - side / 2);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = '#d8d0b6';
          ctx.beginPath();
          ctx.moveTo(x + side / 2, y - side / 2);
          ctx.lineTo(x + side / 2 + lid, y - side / 2 - lid);
          ctx.lineTo(x + side / 2 + lid, y + side / 2 - lid);
          ctx.lineTo(x + side / 2, y + side / 2);
          ctx.closePath();
          ctx.fill();
        }
        return;
      }
      case 'harkis': {
        // Crumble: a lot of small pale pieces rather than a few big ones. It
        // is the pulled pork's opposite number in the row and it is beaten by
        // exactly two things - it is beige instead of red, and its pieces are
        // round instead of long.
        for (const [x, y, rr] of [
          [-r * 0.46, r * 0.32, r * 0.26],
          [-r * 0.06, r * 0.44, r * 0.22],
          [r * 0.4, r * 0.3, r * 0.28],
          [-r * 0.5, -r * 0.08, r * 0.24],
          [-r * 0.08, -r * 0.04, r * 0.3],
          [r * 0.46, -r * 0.12, r * 0.22],
          [-r * 0.26, -r * 0.46, r * 0.24],
          [r * 0.2, -r * 0.5, r * 0.26],
        ] as const) {
          ctx.fillStyle = '#c3a469';
          ctx.beginPath();
          ctx.ellipse(x, y, rr, rr * 0.78, x + y, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = 'rgba(140, 112, 62, 0.55)';
          ctx.beginPath();
          ctx.ellipse(x + rr * 0.22, y + rr * 0.2, rr * 0.34, rr * 0.26, x, 0, Math.PI * 2);
          ctx.fill();
        }
        return;
      }
      case 'munakoiso': {
        // One aubergine, whole and lying on its side, because a purple that
        // size is the easiest box in the game to throw away and it should be:
        // not every vegetable has to be a trap, and a row where all five were
        // would be a row nobody reads at all.
        ctx.fillStyle = '#4a2a63';
        ctx.beginPath();
        ctx.ellipse(r * 0.08, r * 0.12, r * 0.78, r * 0.5, -0.22, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(150, 116, 190, 0.5)';
        ctx.beginPath();
        ctx.ellipse(-r * 0.1, -r * 0.06, r * 0.42, r * 0.13, -0.3, 0, Math.PI * 2);
        ctx.fill();
        // The calyx, which is the half of an aubergine that is not a plum.
        ctx.fillStyle = '#3f6b2f';
        ctx.beginPath();
        ctx.ellipse(-r * 0.66, -r * 0.24, r * 0.28, r * 0.22, -0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(-r * 0.88, -r * 0.42, r * 0.16, r * 0.08, -0.5, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
    }
  }

  /** The plate, and what is on it so far. */
  private plate(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    view: AamupalaView,
  ): void {
    const r = PLATE.radius * this.height;
    ctx.fillStyle = 'rgba(20, 24, 32, 0.45)';
    ellipse(ctx, cx, cy + r * PLATE.squash * 0.16, r, r * PLATE.squash);
    ctx.fill();
    ctx.fillStyle = '#e9e4d6';
    ellipse(ctx, cx, cy, r, r * PLATE.squash);
    ctx.fill();
    ctx.strokeStyle = 'rgba(120, 116, 104, 0.7)';
    ctx.lineWidth = Math.max(1, r * 0.03);
    ellipse(ctx, cx, cy, r * 0.78, r * PLATE.squash * 0.78);
    ctx.stroke();

    // Each helping as the thing it actually was, laid across the plate from the
    // back forwards so that the newest one is the one nearest the eye. Drawn
    // from `served` rather than from a count, because with three meats in the
    // pan a plate of three identical sausages would be a lie about the round
    // you just played - and this is the only place in the game that says which
    // three you got.
    for (let i = 0; i < view.served.length; i++) {
      const t = view.servings === 1 ? 0 : i / (view.servings - 1);
      ctx.save();
      ctx.translate(cx + (t - 0.5) * r * 0.5, cy + (t - 0.4) * r * PLATE.squash * 0.75);
      ctx.rotate(-0.5 + t * 0.55);
      this.food(ctx, view.served[i], r * 0.34);
      ctx.restore();
    }
  }

  /* ------------------------------------------------------------ the mess -- */

  private mess(ctx: CanvasRenderingContext2D, age: number): void {
    // The wash first, so the gouts sit on top of it rather than under it.
    const wash = nausea(age);
    if (wash > 0) {
      ctx.fillStyle = rgba(OKSENNUS.wash, wash * OKSENNUS.washAlpha);
      ctx.fillRect(0, 0, this.width, this.height);
    }

    // Sharp into the offscreen, then one blur for the whole heave: it is the
    // same eye and the same mouthful every time. Browsers with no `filter`
    // draw it sharp, which is worse and still vomit.
    const sharp = this.messCtx;
    if (!sharp) return;
    sharp.setTransform(this.ratio, 0, 0, this.ratio, 0, 0);
    sharp.clearRect(0, 0, this.width, this.height);
    for (const seed of this.seeds) {
      const pose = sampleGout(seed, age, this.pose);
      if (pose.opacity <= 0) continue;
      this.gout(sharp, seed, pose);
    }

    ctx.filter = this.softness;
    ctx.drawImage(this.messCanvas, 0, 0, this.width, this.height);
    ctx.filter = 'none';
    ctx.globalAlpha = 1;
  }

  private gout(ctx: CanvasRenderingContext2D, seed: GoutSeed, pose: GoutPose): void {
    const x = pose.x * this.width;
    const y = pose.y * this.height;
    const radius = pose.radius * this.height;
    const drip = pose.drip * this.height;
    ctx.globalAlpha = pose.opacity;

    // What it left behind on the way down, drawn first so the gout sits on top
    // of its own trail.
    if (drip > 1) {
      const half = radius * OKSENNUS.dripWidth;
      ctx.beginPath();
      ctx.moveTo(x - half, y);
      ctx.quadraticCurveTo(x - half * 0.3, y - drip * 0.5, x, y - drip);
      ctx.quadraticCurveTo(x + half * 0.3, y - drip * 0.5, x + half, y);
      ctx.closePath();
      ctx.fillStyle = VOMIT_EDGE;
      ctx.fill();
    }

    ctx.fillStyle = thick(ctx, x, y, radius);
    ctx.beginPath();
    for (let step = 0; step <= OUTLINE_STEPS; step++) {
      const theta = (step / OUTLINE_STEPS) * Math.PI * 2;
      const rr = radius * outlineRadius(seed, theta);
      const px = x + Math.cos(theta + seed.heading) * rr;
      const py = y + Math.sin(theta + seed.heading) * rr;
      if (step === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();

    for (const speck of seed.specks) {
      const speckRadius = Math.max(0.5, speck.radius * radius);
      const speckX = x + Math.cos(speck.angle) * speck.distance * radius;
      const speckY = y + Math.sin(speck.angle) * speck.distance * radius;
      ctx.fillStyle = thick(ctx, speckX, speckY, speckRadius);
      ctx.beginPath();
      ctx.arc(speckX, speckY, speckRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/** A browned sausage lying along +x, `length` long and `thickness` across. */
function sausage(ctx: CanvasRenderingContext2D, length: number, thickness: number): void {
  const half = length / 2;
  const r = thickness / 2;
  ctx.fillStyle = '#8f4a26';
  ctx.beginPath();
  ctx.moveTo(-half + r, -r);
  ctx.lineTo(half - r, -r);
  ctx.arc(half - r, 0, r, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(-half + r, r);
  ctx.arc(-half + r, 0, r, Math.PI / 2, -Math.PI / 2);
  ctx.closePath();
  ctx.fill();
  // The one highlight, because a sausage with no sheen on it is a twig.
  ctx.fillStyle = 'rgba(216, 150, 96, 0.55)';
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.35, half * 0.62, r * 0.22, 0, 0, Math.PI * 2);
  ctx.fill();
}

/** Flat across the middle, darker from `rimFrom` out. The same idea as `wet`. */
function thick(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
): CanvasGradient {
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
  gradient.addColorStop(0, VOMIT_CORE);
  gradient.addColorStop(OKSENNUS.rimFrom, VOMIT_CORE);
  gradient.addColorStop(1, VOMIT_EDGE);
  return gradient;
}

function ellipse(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
}
